// PriceContainer.js
import React, { useEffect, useState } from "react";
import { ChallengeSelect } from "./ChallengeSelect";
import { ChallengeCard } from "./ChallengeCard";
import { useChallenge } from "../../../context/ChallengeContext";
import { useNavigate } from "react-router-dom";
import TableLoading from "../../../Components/Loader/TableLoading";
import InfiniteScrollFooter from "../../../Components/InfiniteScrollFooter";
import usePaginatedAccounts from "../../../hooks/usePaginatedAccounts";

const title = "چالش ایده‌آل خود را پیدا کنید";
const subtitle = "";

function ChallengeContainer() {
  const navigate = useNavigate();
  const { challengePlans, defaultPlanName, plans, isLoading, error, hasNext, loadingMore, sentinelRef, retry } =
    useChallenge();
  const [selectedPlan, setSelectedPlan] = useState(defaultPlanName);

  useEffect(() => {
    if (!selectedPlan && defaultPlanName) {
      setSelectedPlan(defaultPlanName);
    }
  }, [defaultPlanName, selectedPlan]);

  const fallbackPlan = plans[0];
  const activePlan =
    challengePlans[selectedPlan] ??
    challengePlans[defaultPlanName] ??
    fallbackPlan;

  const handlePlanSelect = (planName) => {
    if (planName && challengePlans[planName]) {
      setSelectedPlan(planName);
    }
  };
  const {
    initialLoading: accountsInitialLoading,
    error: accountsError,
    summary: accountsSummary,
    retry: retryAccounts,
    reload: reloadAccounts,
  } = usePaginatedAccounts({ navigate });
  const realAccountCount = Number(accountsSummary?.real_account_count);
  const hasAccountSummary = Number.isFinite(realAccountCount) && realAccountCount >= 0;

  if (isLoading && !activePlan) {
    return (
      <div className="w-full h-full flex justify-center items-center gap-2 text-zi-text-light dark:text-zi-text-dark">
        در حال دریافت اطلاعات
        <TableLoading />
      </div>
    );
  }

  if (error && !activePlan) {
    return (
      <section className="text-[#fafafa] p-5 min-h-fit w-full overflow-x-hidden">
        <div className="w-full max-w-5xl mx-auto flex flex-col items-center">
          <p className="text-sm text-red-500">
            دریافت اطلاعات چالش ناموفق بود.
          </p>
        </div>
      </section>
    );
  }

  if (!activePlan) {
    return null;
  }

  if (accountsInitialLoading) {
    return (
      <div className="w-full h-full flex justify-center items-center gap-2 text-zi-text-light dark:text-zi-text-dark">
        در حال دریافت اطلاعات
        <TableLoading />
      </div>
    );
  }

  if (accountsError || !hasAccountSummary) {
    return (
      <div className="w-full h-full flex flex-col justify-center items-center gap-3 text-zi-text-light dark:text-zi-text-dark">
        <p className="text-sm text-red-500">دریافت اطلاعات حساب‌ها ناموفق بود.</p>
        <button type="button" onClick={accountsError ? retryAccounts : reloadAccounts} className="text-sm text-cyan-500">تلاش دوباره</button>
      </div>
    );
  }

  if (
    realAccountCount >= 2
  ) {
    return (
      <div className="w-full h-full flex justify-center items-center gap-2 text-zi-text-light dark:text-zi-text-dark">
        همزمان حداکثر دو چالش فعال می توان داشت
      </div>
    );
  }

  return (
    <section className="text-[#fafafa] p-5 min-h-fit w-full overflow-x-hidden">
      <div className="w-full max-w-5xl mx-auto flex flex-col items-center">
        <div>
          <div className="w-full mx-auto text-center">
            <h1 className="text-2xl sm:text-4xl font-medium text-zi-text-light dark:text-zi-text-dark pb-2.5">
              {title}
            </h1>
            <p className="text-base md:text-lg text-[#fafafa]/80 mx-auto">
              {subtitle}
            </p>
          </div>
        </div>
        <ChallengeSelect
          plans={plans}
          onPlanSelect={handlePlanSelect}
          selectedPlan={activePlan.planName}
        />
        <div className="card-divider w-full my-3 sm:my-5 h-px bg-zi-gradient-divider" />
        <ChallengeCard plan={activePlan} />
        <InfiniteScrollFooter sentinelRef={sentinelRef} loading={loadingMore} error={error} onRetry={retry} hasNext={hasNext} />
      </div>
    </section>
  );
}

export { ChallengeContainer };
